/**
 * Enhanced API service with error handling, retry logic, and request optimization.
 */
import axios from 'axios';
import errorHandler from './errorHandler';
import logger from './logger';

class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    this.timeout = 10000; // 10 seconds
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second

    // Create axios instance
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Set up request interceptor
    this.client.interceptors.request.use(
      config => this.handleRequest(config),
      error => this.handleRequestError(error),
    );

    // Set up response interceptor
    this.client.interceptors.response.use(
      response => this.handleResponse(response),
      error => this.handleResponseError(error),
    );

    // Request cache for GET requests
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Handle outgoing requests
   * @param {Object} config - Axios request config
   * @returns {Object} Modified config
   */
  handleRequest(config) {
    // Add authentication token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request ID for tracking
    config.headers['X-Request-ID'] = this.generateRequestId();

    // Log API request
    logger.logEvent('api_request', {
      method: config.method?.toUpperCase(),
      url: config.url,
      has_auth: !!token,
    });

    return config;
  }

  /**
   * Handle request errors
   * @param {Object} error - Request error
   * @returns {Promise} Rejected promise
   */
  handleRequestError(error) {
    const processedError = errorHandler.handleApiError(error, 'api_request');
    return Promise.reject(processedError);
  }

  /**
   * Handle successful responses
   * @param {Object} response - Axios response
   * @returns {Object} Response data
   */
  handleResponse(response) {
    // Log successful response
    logger.logEvent('api_response', {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      duration: response.config.metadata?.endTime - response.config.metadata?.startTime,
    });

    // Return response data or full response based on structure
    return response.data || response;
  }

  /**
   * Handle response errors
   * @param {Object} error - Response error
   * @returns {Promise} Rejected promise or retry attempt
   */
  async handleResponseError(error) {
    const config = error.config;

    // Don't retry if we've already retried too many times
    if (!config || config.__retryCount >= this.maxRetries) {
      const processedError = errorHandler.handleApiError(error, 'api_response');
      return Promise.reject(processedError);
    }

    // Initialize retry count
    config.__retryCount = config.__retryCount || 0;
    config.__retryCount++;

    // Check if we should retry
    if (this.shouldRetry(error)) {
      // Wait before retrying
      await this.delay(this.retryDelay * config.__retryCount);

      // Log retry attempt
      logger.logEvent('api_retry', {
        method: config.method?.toUpperCase(),
        url: config.url,
        attempt: config.__retryCount,
        error_status: error.response?.status,
      });

      // Retry the request
      return this.client(config);
    }

    const processedError = errorHandler.handleApiError(error, 'api_response');
    return Promise.reject(processedError);
  }

  /**
   * Determine if request should be retried
   * @param {Object} error - Error object
   * @returns {boolean} Whether to retry
   */
  shouldRetry(error) {
    // Don't retry if no response (network error)
    if (!error.response) {
      return true;
    }

    // Retry on server errors and rate limiting
    const retryStatuses = [429, 500, 502, 503, 504];
    return retryStatuses.includes(error.response.status);
  }

  /**
   * Delay execution
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise} Promise that resolves after delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Generate unique request ID
   * @returns {string} Request ID
   */
  generateRequestId() {
    return 'req_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Get cache key for request
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {Object} params - Request parameters
   * @returns {string} Cache key
   */
  getCacheKey(method, url, params = {}) {
    const paramsString = JSON.stringify(params);
    return `${method.toUpperCase()}:${url}:${paramsString}`;
  }

  /**
   * Check if cached response is valid
   * @param {Object} cachedItem - Cached response item
   * @returns {boolean} Whether cache is valid
   */
  isCacheValid(cachedItem) {
    return Date.now() - cachedItem.timestamp < this.cacheTimeout;
  }

  /**
   * Make GET request with caching
   * @param {string} url - Request URL
   * @param {Object} config - Request config
   * @returns {Promise} Response promise
   */
  async get(url, config = {}) {
    const cacheKey = this.getCacheKey('GET', url, config.params);
    const cached = this.cache.get(cacheKey);

    // Return cached response if valid
    if (cached && this.isCacheValid(cached)) {
      logger.logEvent('api_cache_hit', { url });
      return cached.data;
    }

    const response = await this.client.get(url, config);

    // Cache successful GET responses
    this.cache.set(cacheKey, {
      data: response,
      timestamp: Date.now(),
    });

    return response;
  }

  /**
   * Make POST request
   * @param {string} url - Request URL
   * @param {Object} data - Request data
   * @param {Object} config - Request config
   * @returns {Promise} Response promise
   */
  async post(url, data = {}, config = {}) {
    const response = await this.client.post(url, data, config);

    // Invalidate related cache entries
    this.invalidateCache(url);

    return response;
  }

  /**
   * Make PUT request
   * @param {string} url - Request URL
   * @param {Object} data - Request data
   * @param {Object} config - Request config
   * @returns {Promise} Response promise
   */
  async put(url, data = {}, config = {}) {
    const response = await this.client.put(url, data, config);

    // Invalidate related cache entries
    this.invalidateCache(url);

    return response;
  }

  /**
   * Make DELETE request
   * @param {string} url - Request URL
   * @param {Object} config - Request config
   * @returns {Promise} Response promise
   */
  async delete(url, config = {}) {
    const response = await this.client.delete(url, config);

    // Invalidate related cache entries
    this.invalidateCache(url);

    return response;
  }

  /**
   * Upload file with progress tracking
   * @param {string} url - Upload URL
   * @param {FormData} formData - Form data with file
   * @param {Function} onProgress - Progress callback
   * @param {Object} config - Additional config
   * @returns {Promise} Response promise
   */
  async uploadFile(url, formData, onProgress = null, config = {}) {
    const uploadConfig = {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config.headers,
      },
    };

    if (onProgress) {
      uploadConfig.onUploadProgress = progressEvent => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      };
    }

    const response = await this.client.post(url, formData, uploadConfig);

    // Log upload completion
    logger.logEvent('file_upload', {
      url,
      size: formData.get('file')?.size,
      type: formData.get('file')?.type,
    });

    return response;
  }

  /**
   * Invalidate cache entries related to URL
   * @param {string} url - URL to invalidate
   */
  invalidateCache(url) {
    const keysToDelete = [];

    for (const key of this.cache.keys()) {
      if (key.includes(url)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Clear all cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Set authentication token
   * @param {string} token - JWT token
   */
  setAuthToken(token) {
    if (token) {
      localStorage.setItem('token', token);
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem('token');
      delete this.client.defaults.headers.common['Authorization'];
    }

    // Clear cache when auth changes
    this.clearCache();
  }

  /**
   * Clear authentication token
   */
  clearAuthToken() {
    this.setAuthToken(null);
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Whether user is authenticated
   */
  isAuthenticated() {
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }

    try {
      // Check if token is expired
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch (error) {
      return false;
    }
  }

  /**
   * Refresh authentication token
   * @returns {Promise} Refresh promise
   */
  async refreshToken() {
    try {
      const response = await this.post('/auth/refresh');
      if (response.access_token) {
        this.setAuthToken(response.access_token);
        return response;
      }
      throw new Error('No access token in refresh response');
    } catch (error) {
      this.clearAuthToken();
      throw error;
    }
  }

  /**
   * Get current user info
   * @returns {Object|null} User info or null
   */
  getCurrentUser() {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        id: payload.user_id || payload.sub,
        username: payload.username,
        email: payload.email,
        role: payload.role,
        exp: payload.exp,
      };
    } catch (error) {
      return null;
    }
  }
}

// Create singleton instance
const apiService = new ApiService();

export const auth = {
  login: credentials => apiService.post('/auth/login', credentials),
  signup: userData => apiService.post('/auth/signup', userData),
  getProfile: () => apiService.get('/auth/profile'),
  updateProfile: profileData => apiService.put('/auth/profile', profileData),
  forgotPassword: email => apiService.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) =>
    apiService.post(`/auth/reset-password/${token}`, { password }),
};

export default apiService;
